import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { triggerBlobDownload, blobErrorMessage } from "src/utils/download_blob";

export const redemptionOrderSlide = createSlice({
    name: "redemptionOrder",
    initialState: {
        page: 0,
        pageCount: 0,
        listData: [],

        // Export to Excel
        exportLoading: false,
        exportError: null,

        success: false,
        fail: false,

        detailData: null,
        responseData: null,
        errorDetailData: null,

        // Update status
        updateStatusSuccess: false,
        updateStatusFail: false,
        updateStatusResponseData: null,

        // Update shipping
        updateShippingSuccess: false,
        updateShippingFail: false,
        updateShippingResponseData: null,

        // Cancel order
        cancelOrderLoading: false,
        cancelOrderSuccess: false,
        cancelOrderFail: false,
        cancelOrderResponseData: null,

        // Statistics
        statisticsData: null,
    },
    reducers: {
        doExportRedemptionOrderRequest: (state) => {
            state.exportLoading = true;
            state.exportError = null;
        },
        doExportRedemptionOrderSuccess: (state) => {
            state.exportLoading = false;
        },
        doExportRedemptionOrderFail: (state, action) => {
            state.exportLoading = false;
            state.exportError = action.payload || "Export failed. Please try again.";
        },

        doGetRedemptionOrderListSuccess: (state, action) => {
            state.listData = action.payload.data;
            state.page = action.payload?.current_page || 1;
            state.pageCount = action.payload?.total_pages || 0;
        },
        doGetRedemptionOrderListFail: (state, action) => {
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
        },

        doGetRedemptionOrderDetailSuccess: (state, action) => {
            state.detailData = action.payload.data;
        },
        doGetRedemptionOrderDetailFail: (state, action) => {
            state.detailData = null;
        },

        doUpdateRedemptionOrderStatusSuccess: (state, action) => {
            state.updateStatusSuccess = true;
            state.updateStatusFail = false;
            state.updateStatusResponseData = action.payload;
        },
        doUpdateRedemptionOrderStatusFail: (state, action) => {
            state.updateStatusSuccess = false;
            state.updateStatusFail = true;
            state.errorDetailData = action.payload;
        },

        doUpdateRedemptionShippingInfoSuccess: (state, action) => {
            state.updateShippingSuccess = true;
            state.updateShippingFail = false;
            state.updateShippingResponseData = action.payload;
        },
        doUpdateRedemptionShippingInfoFail: (state, action) => {
            state.updateShippingSuccess = false;
            state.updateShippingFail = true;
            state.errorDetailData = action.payload;
        },

        doGetRedemptionOrderStatisticsSuccess: (state, action) => {
            state.statisticsData = action.payload.data;
        },
        doGetRedemptionOrderStatisticsFail: (state, action) => {
            state.statisticsData = null;
        },

        doCancelRedemptionOrderRequest: (state) => {
            state.cancelOrderLoading = true;
            state.cancelOrderSuccess = false;
            state.cancelOrderFail = false;
        },
        doCancelRedemptionOrderSuccess: (state, action) => {
            state.cancelOrderLoading = false;
            state.cancelOrderSuccess = true;
            state.cancelOrderFail = false;
            state.cancelOrderResponseData = action.payload;
        },
        doCancelRedemptionOrderFail: (state, action) => {
            state.cancelOrderLoading = false;
            state.cancelOrderSuccess = false;
            state.cancelOrderFail = true;
            state.errorDetailData = action.payload;
        },

        resetRedemptionOrderStateSuccess: (state, action) => {
            state.success = false;
            state.fail = false;
            state.errorDetailData = null;
            state.updateStatusSuccess = false;
            state.updateStatusFail = false;
            state.updateStatusResponseData = null;
            state.updateShippingSuccess = false;
            state.updateShippingFail = false;
            state.updateShippingResponseData = null;
            state.cancelOrderSuccess = false;
            state.cancelOrderFail = false;
            state.cancelOrderResponseData = null;
        },

        resetRedemptionOrderSuccess: (state, action) => {
            state.detailData = null;
        },
    },
});

export const {
    doGetRedemptionOrderListSuccess,
    doGetRedemptionOrderListFail,
    doGetRedemptionOrderDetailSuccess,
    doGetRedemptionOrderDetailFail,
    doUpdateRedemptionOrderStatusSuccess,
    doUpdateRedemptionOrderStatusFail,
    doUpdateRedemptionShippingInfoSuccess,
    doUpdateRedemptionShippingInfoFail,
    doGetRedemptionOrderStatisticsSuccess,
    doGetRedemptionOrderStatisticsFail,
    doCancelRedemptionOrderRequest,
    doCancelRedemptionOrderSuccess,
    doCancelRedemptionOrderFail,
    resetRedemptionOrderStateSuccess,
    resetRedemptionOrderSuccess,
    doExportRedemptionOrderRequest,
    doExportRedemptionOrderSuccess,
    doExportRedemptionOrderFail,
} = redemptionOrderSlide.actions;

// Thunks
export const doGetRedemptionOrderList = (param) => async (dispatch) => {
    try {
        let url = `/api/admin/redemption-order?page=${param.page}&size=${param.limit || 20}`;

        if (param.status) {
            url += `&status=${encodeURIComponent(param.status)}`;
        }
        if (param.search) {
            url += `&search=${encodeURIComponent(param.search)}`;
        }

        const response = await axios.get(url);
        dispatch(doGetRedemptionOrderListSuccess(response.data));
    } catch (error) {
        dispatch(doGetRedemptionOrderListFail(error.response));
    }
};

export const doGetRedemptionOrderDetail = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/redemption-order/${param.order_id}`;
        const response = await axios.get(url);
        dispatch(doGetRedemptionOrderDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetRedemptionOrderDetailFail(error.response));
    }
};

export const doUpdateRedemptionOrderStatus = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/redemption-order/${param.order_id}/status`;
        const response = await axios.put(url, {
            order_status: param.order_status
        });
        dispatch(doUpdateRedemptionOrderStatusSuccess(response.data));
    } catch (error) {
        dispatch(doUpdateRedemptionOrderStatusFail(error.response));
    }
};

export const doUpdateRedemptionShippingInfo = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/redemption-order/${param.order_id}/shipping`;
        const response = await axios.put(url, {
            tracking_number: param.tracking_number,
            shipping_carrier: param.shipping_carrier,
            order_status: param.order_status || 'shipped'
        });
        dispatch(doUpdateRedemptionShippingInfoSuccess(response.data));
    } catch (error) {
        dispatch(doUpdateRedemptionShippingInfoFail(error.response));
    }
};

export const doGetRedemptionOrderStatistics = () => async (dispatch) => {
    try {
        const url = `/api/admin/redemption-order/statistics`;
        const response = await axios.get(url);
        dispatch(doGetRedemptionOrderStatisticsSuccess(response.data));
    } catch (error) {
        dispatch(doGetRedemptionOrderStatisticsFail(error.response));
    }
};

export const doCancelRedemptionOrder = (param) => async (dispatch) => {
    dispatch(doCancelRedemptionOrderRequest());

    try {
        const url = `/api/admin/redemption-order/${param.order_id}/cancel`;
        const response = await axios.post(url, {
            cancel_reason: param.cancel_reason
        });
        dispatch(doCancelRedemptionOrderSuccess(response.data));
        return response.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "Failed to cancel redemption order";
        dispatch(doCancelRedemptionOrderFail(errorMessage));
        throw new Error(errorMessage);
    }
};

// Selectors
export const getRedemptionOrderList = (state) => state.redemptionOrder?.listData || [];
export const getRedemptionOrderListPage = (state) => state.redemptionOrder?.page || 1;
export const getRedemptionOrderListPageCount = (state) => state.redemptionOrder?.pageCount || 0;
export const getRedemptionOrderDetail = (state) => state.redemptionOrder?.detailData || null;
export const getRedemptionOrderSuccess = (state) => state.redemptionOrder?.success || false;
export const getRedemptionOrderFail = (state) => state.redemptionOrder?.fail || false;
export const getRedemptionOrderErrorDetail = (state) => state.redemptionOrder?.errorDetailData || null;

export const getUpdateRedemptionOrderStatusSuccess = (state) => state.redemptionOrder?.updateStatusSuccess || false;
export const getUpdateRedemptionOrderStatusFail = (state) => state.redemptionOrder?.updateStatusFail || false;

export const getUpdateRedemptionShippingSuccess = (state) => state.redemptionOrder?.updateShippingSuccess || false;
export const getUpdateRedemptionShippingFail = (state) => state.redemptionOrder?.updateShippingFail || false;

export const getCancelRedemptionOrderLoading = (state) => state.redemptionOrder?.cancelOrderLoading || false;
export const getCancelRedemptionOrderSuccess = (state) => state.redemptionOrder?.cancelOrderSuccess || false;
export const getCancelRedemptionOrderFail = (state) => state.redemptionOrder?.cancelOrderFail || false;
export const getCancelRedemptionOrderResponse = (state) => state.redemptionOrder?.cancelOrderResponseData || null;

export const getRedemptionOrderStatistics = (state) => state.redemptionOrder?.statisticsData || null;

export default redemptionOrderSlide.reducer;

// Export the current filter set (ALL pages, not just the visible one) as an .xlsx.
export const doExportRedemptionOrder = (param = {}) => async (dispatch) => {
    dispatch(doExportRedemptionOrderRequest());
    try {
        const params = {};
        if (param.status) params.status = param.status;
        if (param.search) params.search = param.search;
        const response = await axios.get("/api/admin/redemption-order/export", { params, responseType: "blob" });
        triggerBlobDownload(response, "Redemption_Orders");
        dispatch(doExportRedemptionOrderSuccess());
    } catch (error) {
        dispatch(doExportRedemptionOrderFail(await blobErrorMessage(error)));
    }
};

export const getExportRedemptionOrderLoading = (state) => state.redemptionOrder?.exportLoading || false;
export const getExportRedemptionOrderError = (state) => state.redemptionOrder?.exportError || null;
